import { lowerCase, snakeCase, upperFirst } from "lodash";
/**
 * patterned after jinja2 capitalize filter
 */
export function capitalize(str: string): string {
  return str.length ? `${str.charAt(0).toUpperCase()}${str.substr(1).toLowerCase()}` : str;
}

/**
 * Based on https://www.sitepoint.com/trimming-strings-in-javascript/
 */
export function trimRight(str: string, charList = " "): string {
  return str.replace(new RegExp(`[${charList}]+$`), "");
}

/**
 * Based on https://www.sitepoint.com/trimming-strings-in-javascript/
 */
export function trimLeft(str: string, charList = " "): string {
  return str.replace(new RegExp(`^[${charList}]+`), "");
}

/**
 * Based on https://www.sitepoint.com/trimming-strings-in-javascript/
 */
export function trim(str: string, charList: string): string {
  return trimLeft(trimRight(str, charList), charList);
}

/**
 * Based on https://stackoverflow.com/questions/9928679/remove-the-string-on-the-beginning-of-an-url/9928725
 */
export function removePrefix(str: string, prefix: string) {
  const hasPrefix = str.startsWith(prefix);
  return hasPrefix ? str.substr(prefix.length) : str;
}

export function parentheticalToMinusSign(amountStr: string): string {
  return amountStr.replace(/^\((.+)\)$/, "-$1");
}

export function minusSignToParenthetical(amountStr: string): string {
  return amountStr.replace(/^-(.+)/, "($1)");
}

export function truncate(str: string, maxLength: number) {
  return str.length > maxLength ? `${str.substring(0, maxLength - 3)}...` : str;
}

/**
 * Based on https://stackoverflow.com/questions/52395822/splitting-a-string-into-multiple-lines-in-javascript
 * @param str
 * @param maxWidth
 */
export function splitIntoLines(str: string, maxWidth: number) {
  function isWhiteSpace(x: string) {
    const WHITE_SPACE = /^\s$/;
    return WHITE_SPACE.test(x.charAt(0));
  }

  let remainder = str;
  const lines = [];

  while (remainder.length >= maxWidth) {
    // Look for white space and break on it, if found
    let found = false;
    for (let i = maxWidth - 1; i >= 0; i--) {
      if (isWhiteSpace(remainder.charAt(i))) {
        lines.push(remainder.slice(0, i));
        remainder = remainder.slice(i + 1);
        found = true;
        break;
      }
    }

    // Whitespace not found, break anyway
    if (!found) {
      lines.push(remainder.slice(0, maxWidth));
      remainder = remainder.slice(maxWidth);
    }
  }
  lines.push(remainder);
  return lines;
}

/**
 * Like a regular join, but with 3 slight improvements:
 * 1. Default separator to ", "
 * 2. Filter null, empty and false values
 * 3. Return empty string if all values falsey
 */
export function join(strings: (string | boolean | null)[], sep = ", ") {
  const filtered = strings.filter(Boolean);
  if (filtered.length === 0) {
    return "";
  }
  return filtered.join(sep);
}

/**
 * Utility function to allow you to return one string or another based on count.
 * If counts are equal, will return the matchedString. Otherwise, it will return
 * the default string.
 */
export function getStringBasedOnCount(
  defaultString: string,
  matchedString: string,
  count: number,
  expected: number,
): string {
  return count === expected ? matchedString : defaultString;
}

/**
 * Takes a camel or snake cased string and generates a first letter capitalized label
 * i.e. 'year_built_or_renovated' -> 'Year built or renovated'
 * i.e. 'yearBuiltOrRenovated' -> 'Year built or renovated'
 * @param name
 */
export function toCapitalizedLabel(name: string): string {
  return upperFirst(lowerCase(snakeCase(name)));
}

export const oxfordComma = (items: string[]) => {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  const itemsCopy = [...items];
  const last = itemsCopy.pop();
  const first = itemsCopy.join(", ");
  return `${first}, and ${last}`;
};

/**
 *
 * See https://en.wikipedia.org/wiki/ABA_routing_transit_number#Check_digit
 * Validates whether a routing number is valid
 */
export const abaChecksum = (routingNumber?: string) => {
  if (!routingNumber) {
    return false;
  }
  if (routingNumber.length !== 9) {
    return false;
  }
  const cArray = routingNumber.split("").map((c) => parseInt(c, 10));
  const cSum =
    3 * (cArray[0] + cArray[3] + cArray[6]) +
    7 * (cArray[1] + cArray[4] + cArray[7]) +
    (cArray[2] + cArray[5] + cArray[8]);
  return cSum % 10 === 0;
};
